/**
 * Money — integer minor units, always. SPEC §3.1, CLAUDE.md §3.
 *
 * Shopify returns money as decimal strings ("1234.56"). Every conversion in this product goes
 * through `parseMoney`, which is pure string math: no `parseFloat`, no `Number()`, no rounding.
 * A float never touches a monetary value anywhere in the system.
 */
/** Currencies are stored with their ISO 4217 minor-unit exponent. v1 supports exponent 2 and 0. */
export const DEFAULT_EXPONENT = 2;
/**
 * Zero-decimal currencies (ISO 4217 exponent 0) that Shopify supports.
 * Anything not listed is treated as exponent 2 — the overwhelming default.
 * Exponent-3 currencies (BHD, KWD, OMR, TND, JOD, LYD) are deliberately unsupported in v1 and
 * throw, rather than silently mis-scaling a balance (SPEC §3.1 [ASSUMPTION]).
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'JPY',
    'KMF',
    'KRW',
    'MGA',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF',
]);
const THREE_DECIMAL_CURRENCIES = new Set([
    'BHD',
    'IQD',
    'JOD',
    'KWD',
    'LYD',
    'OMR',
    'TND',
]);
export class MoneyParseError extends Error {
    input;
    constructor(input, reason) {
        super(`Cannot parse money value ${JSON.stringify(input)}: ${reason}`);
        this.input = input;
        this.name = 'MoneyParseError';
    }
}
export class UnsupportedCurrencyError extends Error {
    currency;
    constructor(currency) {
        super(`Currency ${currency} uses 3 minor-unit decimals, which is not supported in v1 (SPEC §3.1).`);
        this.currency = currency;
        this.name = 'UnsupportedCurrencyError';
    }
}
/** Minor-unit exponent for an ISO 4217 code. Throws for exponent-3 currencies (unsupported in v1). */
export function currencyExponent(currencyCode) {
    const code = currencyCode.toUpperCase();
    if (THREE_DECIMAL_CURRENCIES.has(code))
        throw new UnsupportedCurrencyError(code);
    return ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : DEFAULT_EXPONENT;
}
const DECIMAL_PATTERN = /^([+-]?)(\d+)(?:\.(\d+))?$/;
/**
 * Parse a decimal money string into integer minor units.
 *
 * Accepts: "0", "12", "1234.56", "-1234.5", "+7.00".
 * Rejects: "", " 1", "1,234.56", "1e3", "1.2.3", ".5", "1.", "NaN", "Infinity".
 * Extra fraction digits beyond the currency exponent are allowed only when they are all zeros
 * ("1234.5000" at exponent 2 → 123450); a non-zero digit past the exponent is an error rather than
 * a silent rounding decision.
 */
export function parseMoney(amount, exponent = DEFAULT_EXPONENT) {
    if (typeof amount !== 'string')
        throw new MoneyParseError(String(amount), 'not a string');
    if (exponent < 0 || exponent > 2 || !Number.isInteger(exponent)) {
        throw new MoneyParseError(amount, `unsupported minor-unit exponent ${exponent}`);
    }
    const match = DECIMAL_PATTERN.exec(amount);
    if (!match)
        throw new MoneyParseError(amount, 'not a plain decimal number');
    const [, sign = '', whole = '', fraction = ''] = match;
    let minorDigits = fraction;
    if (minorDigits.length > exponent) {
        const overflow = minorDigits.slice(exponent);
        if (/[^0]/.test(overflow)) {
            throw new MoneyParseError(amount, `more than ${exponent} significant fraction digits — refusing to round money`);
        }
        minorDigits = minorDigits.slice(0, exponent);
    }
    minorDigits = minorDigits.padEnd(exponent, '0');
    const magnitude = BigInt(`${whole}${minorDigits}`);
    return sign === '-' ? -magnitude : magnitude;
}
/** Parse using the currency's own exponent (throws for unsupported exponent-3 currencies). */
export function parseMoneyForCurrency(amount, currencyCode) {
    return parseMoney(amount, currencyExponent(currencyCode));
}
/**
 * Format integer minor units back to a decimal string ("123456" → "1234.56").
 * Display-only: never feed the result back into arithmetic.
 */
export function formatMoney(cents, exponent = DEFAULT_EXPONENT) {
    const negative = cents < 0n;
    const digits = (negative ? -cents : cents).toString().padStart(exponent + 1, '0');
    const cut = digits.length - exponent;
    const whole = digits.slice(0, cut);
    const fraction = digits.slice(cut);
    const body = exponent === 0 ? whole : `${whole}.${fraction}`;
    return negative ? `-${body}` : body;
}
/** Format with the ISO code appended, e.g. "1234.56 USD" — the checkout decline-message format. */
export function formatMoneyWithCurrency(cents, currencyCode) {
    return `${formatMoney(cents, currencyExponent(currencyCode))} ${currencyCode.toUpperCase()}`;
}
/** Absolute value in minor units. */
export function absCents(cents) {
    return cents < 0n ? -cents : cents;
}
/** Sum of minor units. Empty input is 0n, never NaN. */
export function sumCents(values) {
    let total = 0n;
    for (const value of values)
        total += value;
    return total;
}
/** `max(0, value)` — used for "available credit", which is never negative in copy. */
export function clampToZero(cents) {
    return cents < 0n ? 0n : cents;
}
//# sourceMappingURL=money.js.map